import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Callout, Card, Heading, Text } from '../ui/index.js';
import type { Role } from '@goodbuild/shared';
import { api } from '../api/client.js';
import { keys, useAccess, useGames } from '../api/hooks.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { FocusableTextField } from '../components/focus/OnScreenKeyboard.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { errorMessage } from '../lib/errors.js';

export const Route = createFileRoute('/admin/roles')({ component: AdminRoles });

interface StaffRow {
  user: { id: string; username: string; displayName: string | null; role: Role };
  games: Array<{ id: string; slug: string; title: string }>;
}

/**
 * Staff and their roles (`01-product-rules.md` section 7).
 *
 * Only `dev` is handed out here. `admin` comes from the ADMIN_DISCORD_IDS secret and is applied
 * on sign in, so there is no request this page could make that would grant it.
 */
function AdminRoles() {
  const qc = useQueryClient();
  const { isAdmin } = useAccess();
  const games = useGames();

  const [username, setUsername] = React.useState('');
  const [role, setRole] = React.useState<Role>('dev');
  const [gameIds, setGameIds] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const staff = useQuery({
    queryKey: keys.admin.roles,
    queryFn: () => api.get<{ staff: StaffRow[] }>('/admin/roles'),
    enabled: isAdmin,
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: keys.admin.roles });

  const setRoleFor = useMutation({
    mutationFn: () =>
      api.post<{ userId: string; role: Role }>('/admin/roles', {
        username: username.trim(),
        role,
        gameIds,
      }),
    onSuccess: () => {
      setUsername('');
      setGameIds([]);
      setError(null);
      refresh();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const removeRole = useMutation({
    mutationFn: (userId: string) => api.delete(`/admin/roles/${userId}`),
    onSuccess: refresh,
    onError: (err) => setError(errorMessage(err)),
  });

  if (!isAdmin) return <EmptyState title={'You cannot do that.'} />;
  if (staff.isLoading) return <Loading />;

  const rows = staff.data?.staff ?? [];

  return (
    <div className="gb-stack">
      <Callout.Root color="amber">
        <Callout.Description>
          {
            'Admin is set by the ADMIN_DISCORD_IDS secret and applied at sign in. It cannot be granted or removed from here.'
          }
        </Callout.Description>
      </Callout.Root>

      {error ? (
        <Callout.Root color="danger">
          <Callout.Description>{error}</Callout.Description>
        </Callout.Root>
      ) : null}
      <Card size="3">
        <Heading size="4">{'Roles'}</Heading>
        <FocusableTextField
          value={username}
          onChange={setUsername}
          label={'Username'}
          maxLength={64}
          autoFocus
        />

        <Text size="2" color="gray">
          {'Role'}
        </Text>
        <div className="gb-row">
          {(['member', 'dev'] as const).map((value) => (
            <Focusable key={value} onSelect={() => setRole(value)} selectLabel={value}>
              <Button size="2" variant={role === value ? 'solid' : 'surface'}>
                {value}
              </Button>
            </Focusable>
          ))}
        </div>

        {role === 'dev' ? (
          <>
            <Text size="2" color="gray">
              {'Linked games'}
            </Text>
            <div className="gb-row">
              {(games.data ?? []).map((game) => (
                <Focusable
                  key={game.id}
                  onSelect={() =>
                    setGameIds((ids) =>
                      ids.includes(game.id) ? ids.filter((g) => g !== game.id) : [...ids, game.id],
                    )
                  }
                  selectLabel={game.title}
                >
                  <Button size="1" variant={gameIds.includes(game.id) ? 'solid' : 'surface'}>
                    {game.title}
                  </Button>
                </Focusable>
              ))}
            </div>
          </>
        ) : null}

        <div style={{ marginTop: '0.5rem' }}>
          <Focusable
            onSelect={() => setRoleFor.mutate()}
            selectLabel={'Save'}
            disabled={!username.trim() || setRoleFor.isPending}
          >
            <Button size="2">{'Save'}</Button>
          </Focusable>
        </div>
      </Card>

      <FocusZone focusKey="admin-staff" direction="column" className="gb-stack">
        {rows.length === 0 ? (
          <EmptyState title={'None'} />
        ) : (
          rows.map((row) => (
            <Card key={row.user.id} size="2">
              <div className="gb-seat">
                <Text size="2">{row.user.displayName ?? row.user.username}</Text>
                <Badge color={row.user.role === 'admin' ? 'sand' : 'gray'}>{row.user.role}</Badge>
                {row.games.map((game) => (
                  <Badge key={game.id} color="gray">
                    {game.title}
                  </Badge>
                ))}
                <span className="gb-seat__spacer" />
                <Focusable
                  onSelect={() => removeRole.mutate(row.user.id)}
                  selectLabel={'Remove role'}
                >
                  <Button size="1" variant="ghost" color="danger">
                    {'Remove role'}
                  </Button>
                </Focusable>
              </div>
            </Card>
          ))
        )}
      </FocusZone>
    </div>
  );
}
